import { verifyPasskeyApproval, } from './approval.js';
import { toBase64Url } from './bytes.js';
import { DEFAULT_CLOCK_SKEW_MS, DEFAULT_ENDORSEMENT_TTL_MS, SESSION_ENDORSEMENT_MAX_TTL_MS, verifyDeviceEndorsement, } from './endorsement.js';
import { createRevocationChallenge, isRevocationList, } from './revocation.js';
/**
 * The daemon's trust state: pinned passkeys (roots), endorsed device keys, revocations and
 * per-credential signCounts. Everything added is verified; nothing is taken on the Worker's word.
 */
export class TrustStore {
    rp;
    clockSkewMs;
    maxEndorsementTtlMs;
    #credentials = new Map();
    #devices = new Map();
    #revokedDevices = new Set();
    #localRevokedDevices = new Set();
    #revokedCredentials = new Set();
    #signCounts = new Map();
    /** The batch challenge last accepted at #signCounts' value (batched step-ups share a count). */
    #batchAt = new Map();
    /** First signed revocation-list seq that revoked an id; used only to replay directory history. */
    #revokedAt = new Map();
    /** Session devices the hub reported ended, with the time first seen (endSessionDevices). */
    #endedSessions = new Map();
    #revocationSeq = 0;
    constructor(options) {
        this.rp = { rpId: options.rp.rpId, origins: [...options.rp.origins] };
        this.clockSkewMs = options.clockSkewMs ?? DEFAULT_CLOCK_SKEW_MS;
        this.maxEndorsementTtlMs = options.maxEndorsementTtlMs ?? DEFAULT_ENDORSEMENT_TTL_MS;
        for (const c of options.credentials)
            this.#credentials.set(c.credentialId, c);
    }
    get revocationSeq() {
        return this.#revocationSeq;
    }
    /** Pinned credentials that are not revoked. */
    credentials() {
        return [...this.#credentials.values()].filter((c) => !this.#revokedCredentials.has(c.credentialId));
    }
    /**
     * Pin an additional passkey. Callers must only do this after a verified `credential.add`
     * command (device signature + step-up by an existing credential), or at enrollment.
     */
    pinCredential(credential) {
        if (this.#revokedCredentials.has(credential.credentialId)) {
            throw new Error('credential was revoked and cannot be re-pinned');
        }
        this.#credentials.set(credential.credentialId, credential);
    }
    isCredentialRevoked(credentialId) {
        return this.#revokedCredentials.has(credentialId);
    }
    /**
     * Whether a signed revocation list with seq <= `seq` revoked this device or credential. Used
     * only to validate superseded directory snapshots; current decisions use isDeviceRevoked.
     * Ids restored from snapshots without history are treated as revoked since seq 0.
     */
    revokedAsOf(id, seq) {
        const at = this.#revokedAt.get(id);
        return at !== undefined && at <= seq;
    }
    /** Cumulative signed revocations, for building the next strictly newer list. */
    revokedDeviceIds() {
        return [...this.#revokedDevices].sort();
    }
    revokedCredentialIds() {
        return [...this.#revokedCredentials].sort();
    }
    /**
     * Verify a passkey approval of a superseded directory snapshot: the credential must be pinned
     * and not revoked by a list at or before `asOfSeq`. Never use this for current authorization.
     */
    async verifyHistoricalPasskeyApproval(assertion, challenge, asOfSeq) {
        return verifyPasskeyApproval(assertion, challenge, {
            credentials: [...this.#credentials.values()],
            rp: this.rp,
            revokedCredentials: new Set([...this.#revokedCredentials].filter((c) => this.revokedAsOf(c, asOfSeq))),
        });
    }
    isDeviceRevoked(deviceKeyId) {
        return this.#revokedDevices.has(deviceKeyId) || this.#localRevokedDevices.has(deviceKeyId);
    }
    /** Revoke a device from the daemon's local CLI (no passkey needed; local user is trusted). */
    revokeDeviceLocally(deviceKeyId) {
        this.#localRevokedDevices.add(deviceKeyId);
        this.#devices.delete(deviceKeyId);
    }
    /**
     * The hub reports "Just this session" devices whose sign-in ended (sign-out, pagehide, removal
     * from Devices). No passkey signs this, so it can only ever *reduce* authority, and only for
     * session-scoped endorsements: a trusted device named here is unaffected (lookupDevice). The
     * hub can already withhold any device's commands, so this gives it nothing new. An entry is
     * kept until any endorsement it could block has expired (12 h cap plus clock skew), then pruned.
     */
    endSessionDevices(deviceKeyIds, now = Date.now()) {
        let changed = false;
        for (const id of deviceKeyIds)
            if (typeof id === 'string' && id && !this.#endedSessions.has(id)) {
                this.#endedSessions.set(id, now);
                changed = true;
            }
        const horizon = SESSION_ENDORSEMENT_MAX_TTL_MS + 2 * this.clockSkewMs;
        for (const [id, at] of this.#endedSessions) {
            const device = this.#devices.get(id);
            if (now - at > horizon && (!device || now > device.expiresAt + this.clockSkewMs)) {
                this.#endedSessions.delete(id);
                changed = true;
            }
        }
        return changed;
    }
    /** Verify a passkey assertion over `challenge` against the pinned, non-revoked credentials. */
    async verifyPasskeyApproval(assertion, challenge, options = {}) {
        const batch = options.batch ? toBase64Url(challenge) : undefined;
        const result = await verifyPasskeyApproval(assertion, challenge, {
            credentials: [...this.#credentials.values()],
            rp: this.rp,
            revokedCredentials: this.#revokedCredentials,
            ...(options.trackSignCount
                ? {
                    previousSignCount: (id) => {
                        const prev = this.#signCounts.get(id);
                        // One batched assertion covers several step-ups: its exact batch may repeat its count.
                        const last = this.#batchAt.get(id);
                        return prev !== undefined && batch && last?.signCount === prev && last.batch === batch
                            ? prev - 1
                            : prev;
                    },
                }
                : {}),
        });
        if (result.ok && options.trackSignCount && result.signCount > 0) {
            this.#signCounts.set(result.credential.credentialId, result.signCount);
            if (batch)
                this.#batchAt.set(result.credential.credentialId, { signCount: result.signCount, batch });
            else
                this.#batchAt.delete(result.credential.credentialId);
        }
        return result;
    }
    /** Verify and remember a device endorsement. */
    async addEndorsement(endorsement, now = Date.now()) {
        const allRevokedDevices = new Set([...this.#revokedDevices, ...this.#localRevokedDevices]);
        const result = await verifyDeviceEndorsement(endorsement, {
            credentials: [...this.#credentials.values()],
            rp: this.rp,
            now,
            clockSkewMs: this.clockSkewMs,
            maxTtlMs: this.maxEndorsementTtlMs,
            revokedCredentials: this.#revokedCredentials,
            revokedDevices: allRevokedDevices,
        });
        if (result.ok) {
            const existing = this.#devices.get(result.device.deviceKeyId);
            // Keep whichever endorsement lasts longer (re-endorsement extends a device), and replace
            // one whose endorsing passkey has since been revoked.
            if (!existing ||
                existing.expiresAt < result.device.expiresAt ||
                this.#revokedCredentials.has(existing.endorsedBy)) {
                this.#devices.set(result.device.deviceKeyId, result.device);
            }
        }
        return result;
    }
    lookupDevice(deviceKeyId, now = Date.now()) {
        if (this.isDeviceRevoked(deviceKeyId))
            return { status: 'revoked', detail: 'device' };
        const device = this.#devices.get(deviceKeyId);
        if (!device)
            return { status: 'unknown' };
        if (device.session && this.#endedSessions.has(deviceKeyId))
            return { status: 'revoked', detail: 'device' };
        if (this.#revokedCredentials.has(device.endorsedBy)) {
            return { status: 'revoked', detail: 'credential' };
        }
        if (now > device.expiresAt + this.clockSkewMs)
            return { status: 'expired', device };
        return { status: 'ok', device };
    }
    devices(now = Date.now()) {
        return [...this.#devices.values()].filter((d) => this.lookupDevice(d.deviceKeyId, now).status === 'ok');
    }
    /**
     * Apply a passkey-signed revocation list. Rules: strictly newer `seq`; signed by a pinned
     * credential that is neither already revoked nor revoked by this list (so at least one
     * credential always survives and a list can't be signed by the key it removes).
     */
    async applyRevocationList(signed, now = Date.now()) {
        const list = signed?.list;
        if (!isRevocationList(list))
            return { ok: false, reason: 'malformed' };
        if (list.seq <= this.#revocationSeq) {
            return { ok: false, reason: 'stale', detail: `seq ${list.seq} <= ${this.#revocationSeq}` };
        }
        if (list.issuedAt > now + this.clockSkewMs)
            return { ok: false, reason: 'not-yet-valid' };
        if (list.credentials.includes(String(signed.assertion?.credentialId))) {
            return { ok: false, reason: 'signer-revoked' };
        }
        const approval = await this.verifyPasskeyApproval(signed.assertion, await createRevocationChallenge(list));
        if (!approval.ok)
            return approval;
        this.#revocationSeq = list.seq;
        for (const d of list.devices) {
            this.#revokedDevices.add(d);
            this.#devices.delete(d);
            if (!this.#revokedAt.has(d))
                this.#revokedAt.set(d, list.seq);
        }
        for (const c of list.credentials) {
            this.#revokedCredentials.add(c);
            if (!this.#revokedAt.has(c))
                this.#revokedAt.set(c, list.seq);
        }
        return {
            ok: true,
            seq: list.seq,
            devices: [...this.#revokedDevices],
            credentials: [...this.#revokedCredentials],
        };
    }
    toJSON() {
        return {
            v: 1,
            rp: { rpId: this.rp.rpId, origins: [...this.rp.origins] },
            credentials: [...this.#credentials.values()],
            endorsements: [...this.#devices.values()].map((d) => d.endorsement),
            revocationSeq: this.#revocationSeq,
            revokedDevices: [...this.#revokedDevices],
            revokedCredentials: [...this.#revokedCredentials],
            localRevokedDevices: [...this.#localRevokedDevices],
            signCounts: [...this.#signCounts],
            batchSignCounts: [...this.#batchAt].map(([id, b]) => [id, b.signCount, b.batch]),
            revokedAt: [...this.#revokedAt],
            ...(this.#endedSessions.size ? { endedSessions: [...this.#endedSessions] } : {}),
        };
    }
    /** Restore from a snapshot. Endorsements are re-verified; expired or invalid ones are dropped. */
    static async fromJSON(snapshot, options = {}) {
        if (snapshot.v !== 1)
            throw new Error('unsupported trust store snapshot version');
        const store = new TrustStore({
            rp: snapshot.rp,
            credentials: snapshot.credentials,
            ...(options.clockSkewMs === undefined ? {} : { clockSkewMs: options.clockSkewMs }),
            ...(options.maxEndorsementTtlMs === undefined
                ? {}
                : { maxEndorsementTtlMs: options.maxEndorsementTtlMs }),
        });
        store.#revocationSeq = snapshot.revocationSeq;
        for (const d of snapshot.revokedDevices)
            store.#revokedDevices.add(d);
        for (const d of snapshot.localRevokedDevices)
            store.#localRevokedDevices.add(d);
        for (const c of snapshot.revokedCredentials)
            store.#revokedCredentials.add(c);
        for (const [id, n] of snapshot.signCounts)
            store.#signCounts.set(id, n);
        for (const [id, n, batch] of snapshot.batchSignCounts ?? [])
            if (store.#signCounts.get(id) === n && typeof batch === 'string')
                store.#batchAt.set(id, { signCount: n, batch });
        for (const [id, seq] of snapshot.revokedAt ?? [])
            if (Number.isSafeInteger(seq) && seq > 0)
                store.#revokedAt.set(id, seq);
        for (const [id, at] of snapshot.endedSessions ?? [])
            if (typeof id === 'string' && Number.isSafeInteger(at))
                store.#endedSessions.set(id, at);
        // Older snapshots have no history: fail closed by treating those ids as always revoked.
        for (const id of [...snapshot.revokedDevices, ...snapshot.revokedCredentials])
            if (!store.#revokedAt.has(id))
                store.#revokedAt.set(id, 0);
        for (const e of snapshot.endorsements)
            await store.addEndorsement(e, options.now ?? Date.now());
        return store;
    }
}
//# sourceMappingURL=trust-store.js.map